import { Directive, EventEmitter, Input, Output } from '@angular/core';
import { BehaviorSubject, of, Subscription } from 'rxjs';
import { delay } from 'rxjs/operators';

export interface TextSectionTextTransformer {
  transformText(text: string): string;
}

export abstract class TextSectionAction {
  abstract title?: string;
  abstract icon?: string;
  abstract available: boolean;
  abstract enabled: boolean;
  abstract doAction?(): void;
}

@Directive()
export abstract class BaseTextSectionComponent {
  private hoverSubscription: Subscription;

  private _text: string;
  private _textTransformed: string;
  private _textTransformer: TextSectionTextTransformer;

  private _afterViewInit: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  get afterViewInit(): BehaviorSubject<any> {
    return this._afterViewInit;
  }

  get text(): string {
    return this._text || "";
  }
  get textTransformed(): string {
    if (this._textTransformed == null) {
      var text = this.text;
      if (this.textTransformer != null) {
        text = this.textTransformer.transformText(this.text)
      }
      this._textTransformed = text;
      this.textTransformedChange.emit(this._textTransformed);
    }
    return this._textTransformed;
  }

  get textTransformer(): TextSectionTextTransformer {
    return this._textTransformer;
  }

  @Input()
  set textTransformer(v: TextSectionTextTransformer) {
    if (this._textTransformer != v) {
      this._textTransformer = v;
      this._textTransformed = null;
      this.textTransformerChange.emit(v);
    }
  }

  @Input()
  set text(v: string) {
    if (this._text != v) {
      this._text = v;
      this._textTransformed = null;
      this.textChange.emit(v);
    }
  }
  @Output()
  textChange: EventEmitter<string> = new EventEmitter<string>();

  @Output()
  textTransformedChange: EventEmitter<string> = new EventEmitter<string>();

  @Output()
  textTransformerChange: EventEmitter<TextSectionTextTransformer> = new EventEmitter<TextSectionTextTransformer>();

  private _format: string;
  get format(): string {
    if (!this._format) {
      return "html";
    }
    return this._format;
  }
  @Input('format')
  set format(v: string) {
    this._format = v;
  }

  private _readOnly: boolean;
  get readOnly(): boolean {
    if (this._readOnly == null) {
      return true;
    }
    return this._readOnly;
  }
  @Input('readOnly')
  set readOnly(v: boolean) {
    this._readOnly = v;
  }

  private _editAvailable: boolean;
  get editAvailable(): boolean {
    if (this._editAvailable == null) {
      return false;
    }
    return this._editAvailable;
  }
  @Input('editAvailable')
  set editAvailable(v: boolean) {
    this._editAvailable = v;
  }


  private _editToggleAvailable: boolean;
  get editToggleAvailable(): boolean {
    if (this._editToggleAvailable == null) {
      return true;
    }
    return this._editToggleAvailable;
  }
  @Input('editToggleAvailable')
  set editToggleAvailable(v: boolean) {
    this._editToggleAvailable = v;
  }

  private _controlsRequested: boolean;
  get controlsRequested(): boolean {
    if (this._controlsRequested == null) {
      return false;
    }
    return this._controlsRequested;
  }
  set controlsRequested(v: boolean) {
    this._controlsRequested = v;
  }

  private _actions: TextSectionAction[] = [];
  get actions(): TextSectionAction[] {
    if (this._actions == null) {
      return [];
    }
    return this._actions;
  }
  @Input('actions')
  set actions(v: TextSectionAction[]) {
    this._actions = v;
  }

  get controlsEnabled(): boolean {
    return this.editAvailable && (this.controlsRequested || !this.readOnly);
  }
  get editToggleEnabled(): boolean {
    return this.editEnabled && this.editToggleAvailable;
  }
  get editEnabled(): boolean {
    return this.editAvailable;
  }
  get editorClasses(): string {
    return this.readOnly ? (this.controlsEnabled ? this.classesViewWithControls : this.classesViewWithoutControls) : this.classesEdit;
  }
  abstract classesEdit: string;
  abstract classesViewWithControls: string;
  abstract classesViewWithoutControls: string;

  constructor() { }

  hoverOver(v: boolean) {
    if (v) {
      this.controlsRequested = true;
      if (this.hoverSubscription) {
        this.hoverSubscription.unsubscribe();
        this.hoverSubscription = null;
      }
    } else {
      this.hoverSubscription = of(null).pipe(delay(1000)).subscribe(() => {
        this.controlsRequested = false;
        if (this.hoverSubscription) {
          this.hoverSubscription.unsubscribe();
          this.hoverSubscription = null;
        }
      });
    }
  }

  ngAfterViewInit() {
    this.afterViewInit.next(null);
  }

  retransformText() {
    this._textTransformed = null;
  }
}
